import { AuthenticationResult } from "@azure/msal-browser";
import localforage from "localforage";

import { AppConfig } from "../config/appConfig";
import { UpdateClaim, UpdateClaimType } from "../types/appHub";

/**
 * Methods for updating and retrieving data from the shared IndexedDB store.
 */
export namespace SharedStoreService {

	/** Methods for storing and retrieving authentication credentials. */
	export namespace Credentials {
		// There will only ever be one row for credentials
		const credentialsId = "1";

		/**
		 * Get the localforage instance for the credentials table.
		 * @returns The localforage instance for credentials.
		 */
		const getTable = () => {
			return localforage.createInstance({
				name: AppConfig.idbStore,
				storeName: AppConfig.idbCredentialsTable,
				description: "Stores authentication credentials as an AuthenticationResult MSAL object."
			});
		}

		/** Clear all credentials from the store. */
		export async function clear() {
			try {
				await getTable().removeItem(credentialsId);
			} catch (error) {
				throw new Error("ERROR: SharedStoreService.Credentials.clear: Error clearing credentials from the store.", error);
			}
		}

		/** 
		 * Retrieve the stored authentication credentials. 
		 * @returns The stored AuthenticationResult object, or null if not found.
		 * */
		export async function get(): Promise<AuthenticationResult | null> {
			try {
				return await getTable().getItem<AuthenticationResult>(credentialsId);
			} catch (error) {
				throw new Error("ERROR: SharedStoreService.Credentials.get: Error retrieving credentials from the store.", error);
			}

		}

		/** Remove the stored authentication credentials. */
		export async function remove(): Promise<void> {
			try {
				return await getTable().removeItem(credentialsId);
			} catch (error) {
				throw new Error("ERROR: SharedStoreService.Credentials.remove: Error removing credentials from the store.", error);
			}

		}
		
		/** 
		 * Store authentication credentials. 
		 * @param credentials The AuthenticationResult object to store.
		 * */
		export async function set(credentials: AuthenticationResult): Promise<void> {
			if (!credentials) throw new Error("Credentials must be provided.");
			try {
				await getTable().setItem(credentialsId, credentials);
			} catch (error) {
				throw new Error("ERROR: SharedStoreService.Credentials.set: Error saving credentials to the store.", error);
			}
		}
	}

	/** Methods for storing and retrieving exclusivity claims from application instances seeking to perform resource updates on the shared store. */
	export namespace UpdateClaims {
		/**
		 * Get the localforage instance for the update claims table.
		 * @returns The localforage instance for update claims.
		 */
		const getTable = () => {
			return localforage.createInstance({
				name: AppConfig.idbStore,
				storeName: AppConfig.idbUpdateClaimsTable,
				description: "Stores temporary exclusivity claims individual application instances have made to update a shared store resource to prevent duplicate updates."
			});
		}

		/**
		 * Clear all update claims from the store.
		 */
		export async function clear() {
			try {
				await getTable().clear();
			} catch (error) {
				throw new Error("ERROR: SharedStoreService.UpdateClaims.clear: Error clearing update claims from the store.", error);
			}
		}

		/**
		 * Retrieve an update claim by type from the store.
		 * @param type The type of update claim.
		 * @returns An UpdateClaim, or null if not found.
		 */
		export async function get(type: UpdateClaimType): Promise<UpdateClaim | null> {
			try {
				const claim = await getTable().getItem<UpdateClaim>(type);
				if (!claim) return null;

				// UpateClaim is a class with some logic in it that doesn't get created by just creating a new object with the same field names
				const newClaim = new UpdateClaim();
				return Object.assign(newClaim, claim);
			} catch (error) {
				throw new Error(`ERROR: SharedStoreService.UpdateClaims.get: Error retrieving update claim "${type}" from the store.`, error);
			}

		}

		/**
		 * Remove an update claim of the same type from the store.
		 * @param type The type of update claim.
		 */
		export async function remove(type: UpdateClaimType): Promise<void> {
			try {
				return await getTable().removeItem(type);
			} catch (error) {
				throw new Error(`ERROR: SharedStoreService.UpdateClaims.remove: Error removing update claim "${type}" from the store.`, error);
			}

		}

		/**
		 * Create a new update claim in the store bound to the requester's instance ID.
		 * @param type The type of resource being updated.
		 * @param data Any additional data associated with the claim. Optional.
		 */
		export async function create(type: UpdateClaimType, data?: any): Promise<void> {
			if (!type) throw new Error("Update claim type must be provided.");
			try {
				await getTable().setItem(type, new UpdateClaim(type, data));
			} catch (error) {
				throw new Error("ERROR: SharedStoreService.UpdateClaims.set: Error saving update claim to the store.", error);
			}
		}

		/**
		 * Modify an existing update claim of the same type in the store.
		 * @param claim The claim to update. Only changes to the "data" property will be saved to maintain claim integrity.
		 */
		export async function update(claim: UpdateClaim): Promise<void> {
			if (!claim) throw new Error("Update claim must be provided.");
			if (!claim.type) throw new Error("Update claim type must be provided.");
			if (!claim.instanceId) throw new Error("Update claim instance ID must be provided.");

			var originalClaim = await get(claim.type);
			if (!originalClaim) throw new Error("Update claim does not exist in the store.");
			if (originalClaim.instanceId !== claim.instanceId) throw new Error(`Current claim of type "${claim.type}" is not owned by this instance and may not be modified.`);
			
			try {
				originalClaim.data = claim.data;
				originalClaim.modified = new Date();
				await getTable().setItem(claim.type, originalClaim);
			} catch (error) {
				throw new Error("ERROR: SharedStoreService.UpdateClaims.update: Error updating update claim in the store.", error);
			}
		}
	}
}